<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1>{{ $t("empresas_de_servicio.titulo") }}{{ $t(`empresas_de_servicio.tipo.${tipoServicioEnum}`) }}</h1>
      <div class="toolbar">
        <button type="button" @click.prevent="crearEmpresaServicios()" class="btn btn-link">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>

    <DxDataGrid v-bind="getDefaultGridProperties()" :data-source="gridSource" ref="grid" class="dx-card wide-card grid-operaciones">
      <DxColumnFixing :enabled="true" />
      <DxColumn data-field="ImagenB64" :caption="$t('empresas_de_servicio.grid.imagen')" cell-template="imagenTemplate" :allow-filtering="false"></DxColumn>
      <DxColumn data-field="Nombre" :caption="$t('empresas_de_servicio.grid.nombre')" data-type="string"></DxColumn>
      <DxColumn data-field="DireccionContacto" :caption="$t('empresas_de_servicio.grid.direccion_contacto')"></DxColumn>
      <DxColumn data-field="NombreContacto" :caption="$t('empresas_de_servicio.grid.nombre_contacto')"></DxColumn>
      <DxColumn data-field="TelContacto" :caption="$t('empresas_de_servicio.grid.telefono_contacto')"></DxColumn>
      <DxColumn data-field="EmailContacto" :caption="$t('empresas_de_servicio.grid.email_contacto')"></DxColumn>
      <DxColumn data-field="Activado" :caption="$t('empresas_de_servicio.grid.estado')" cell-template="estadoTemplate">
        <DxLookup v-bind="getLookupEstadoActivacion()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="SincronizaEsave" :caption="$t('empresas_de_servicio.grid.sincroniza_esave')" cell-template="booleanTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxColumn
        type="buttons"
        :caption="$t('empresas_de_servicio.grid.acciones')"
        :fixed="true"
        fixed-position="right"
        :buttons="[
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('empresas_de_servicio.grid.gestionar'),
            icon: 'fa-solid fa-euro-sign text-success',
            onClick: (e: any) => gestionar(e.row?.data.Id),
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('empresas_de_servicio.grid.modificar'),
            icon: 'fa-regular fa-edit text-success',
            onClick: (e: any) => verDetalle(e.row?.data.Id),
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('empresas_de_servicio.grid.borrar'),
            icon: 'fa-regular fa-trash text-success',
            onClick: (e: any) => borrar(e.row?.data.Id),
          },
        ]"
      />

      <template #imagenTemplate="{ data }">
        <img :src="`${data.value}`" style="max-width: 100px; max-height: 30px" alt="Imagen" />
      </template>
      <template #estadoTemplate="{ data }">
        <div :class="[`d-inline-block rounded py-1 px-2 my-1`, `estado ${data.value ? 'activado' : 'desactivado'}`]">
          {{ $t(`empresas_de_servicio.estado.${data.value ? "activado" : "desactivado"}`) }}
        </div>
      </template>
      <template #booleanTemplate="{ data }">
        <BooleanTemplate :value="data.value" />
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { DxDataGrid, DxColumn, DxLookup, DxColumnFixing } from "devextreme-vue/data-grid";
import { getDefaultGridProperties, getColumnLookupBoolean } from "@helpers/grid-helper";
import { useApi } from "@api/integration";
import type CustomStore from "devextreme/data/custom_store";
import { useIntl } from "@services/i18n";
import { useModalService } from "@services/modal-service";
import EmpresasServiciosEnergiaForm from "@/components/empresas-servicios/energia/EmpresasServiciosEnergiaForm.vue";
import { EmpresaServiciosFactory } from "@/components/empresas-servicios/models/EmpresaServiciosFactory";
import { messageBoxResult } from "@components/dialogs/message-box";
import type { ColumnLookup } from "devextreme/ui/data_grid";
import { type DXLoadOptions } from "@/.shared/.api";
import { TipoEmpresaServiciosEnum } from "@api/api";
import { useRouter } from "@services/routing-services";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import BooleanTemplate from "@/.shared/components/BooleanTemplate.vue";

const intl = useIntl();
const api = useApi();
const grid = ref<DxDataGrid>();
const gridSource = ref<CustomStore>();
const modal = useModalService();
const router = useRouter();

const props = defineProps({
  tipoServicio: String,
});

const tipoServicioEnum = computed(() => props.tipoServicio?.toUpperCase() as TipoEmpresaServiciosEnum);

onMounted(async () => {
  setDataSource();
});

function getLookupEstadoActivacion(): ColumnLookup {
  return {
    dataSource: [
      {
        value: true,
        text: intl.i18n.t(`empresas_de_servicio.estado.activado`),
      },
      {
        value: false,
        text: intl.i18n.t(`empresas_de_servicio.estado.desactivado`),
      },
    ],
    valueExpr: "value",
    displayExpr: "text",
  };
}

async function crearEmpresaServicios() {
  let title = `${intl.i18n.t("empresas_de_servicios.creacion.titulo_energia")}`;
  modal
    .showModal(
      EmpresasServiciosEnergiaForm,
      { model: EmpresaServiciosFactory.createEmpresaServiciosEnergia(), isEditMode: false },
      { title: title, width: 800, height: 700 },
    )
    .then((value) => {
      if (value == messageBoxResult.ok) grid.value?.instance?.refresh();
    });
}

const setDataSource = async () => {
  gridSource.value = api.apiStore({
    key: "Id",
    load: async (p) => {
      const q = p as DXLoadOptions;
      return await api.callStandardApi("getEmpresasServiciosEnergiaForGrid", { showLoading: false }, q);
    },
  });
};

const gestionar = async (idEmpresaServicio: string) => {
  await router.push(
    intl.i18Route({ name: "empresas-servicios-gestionar", params: { tipoServicio: props.tipoServicio, idEmpresaServicios: idEmpresaServicio } }),
  );
};

const verDetalle = async (idEmpresaServicio: string) => {
  let title = `${intl.i18n.t("empresas_de_servicios.edicion.titulo_energia")}`;
  const empresaData = await api.callStandardApi("getEmpresaServiciosEnergiaById", { showLoading: true }, idEmpresaServicio);
  modal
    .showModal(
      EmpresasServiciosEnergiaForm,
      { model: EmpresaServiciosFactory.createFromDetail(empresaData), isEditMode: true },
      { title: title, width: 800, height: 700 },
    )
    .then((value) => {
      if (value == messageBoxResult.ok) grid.value?.instance?.refresh();
    });
};

const borrar = async (idEmpresaServicio: string) => {
  let respuesta = await modal.messageBox(
    intl.i18n.t("empresas_de_servicios.grid.borrar_pregunta"),
    intl.i18n.t("empresas_de_servicios.grid.titulo_confirma_borrar"),
    "okCancel",
    "warning",
  );
  if (respuesta == messageBoxResult.ok)
    await api
      .callStandardApi("deleteEmpresaServicios", { showLoading: true }, idEmpresaServicio)
      .then(() => {
        toastSuccess(intl.i18n.t("empresa_de_servicio.borrado_exito"));
        grid.value?.instance?.refresh();
      })
      .catch(() => toastError(intl.i18n.t("empresa_de_servicio.borrado_fallo")));
};

const resetGrid = () => {
  grid.value?.instance?.clearFilter();
  grid.value?.instance?.clearGrouping();
  grid.value?.instance?.clearSelection();
  grid.value?.instance?.clearSorting();
};

defineExpose({ resetGrid });
</script>

<style lang="scss" scoped>


</style>
